import api from '../api';

// Cache for decoded token information
let tokenCache = null;

/**
 * Normalizes verify-token API user payload and persists to localStorage so {@link TokenDecode}
 * trial/membership logic matches the same server response used for the header "Modules Available".
 *
 * @param {object} u - `response.data.user` from `/api/v1/auth/verify-token`
 * @returns {object|null} Normalized user object written to `userInfo`, or null if `u` is missing
 */
export function persistUserInfoFromVerifyTokenUser(u) {
  if (!u) {
    return null;
  }
  const normalizedUser = {
    UserEmail: u.email,
    User_Id: u.userId,
    Session_Id: u.sessionId,
    Status: u.status,
    MemberShip: u.membership,
    Features: u.features,
    showProxyIcon: u.showProxyIcon,
    showActivistIcon: u.showActivistIcon,
  };
  window.localStorage.setItem('userInfo', JSON.stringify(normalizedUser));
  return normalizedUser;
}

/**
 * Fetches verify-token and persists userInfo for the current session token.
 * Used after login so access state matches the new token without clearing the session on transient errors.
 * On failure, clears cached userInfo and in-memory token cache only (does not remove token / userEmail).
 *
 * @param {string} token - Session token (same as stored in localStorage)
 * @returns {Promise<object|null>} User payload from verify-token, or null if invalid/unavailable
 */
export async function refreshUserInfoFromToken(token) {
  let accessToken = token ?? localStorage.getItem('token');
  if (!accessToken) {
    const { tryRefreshSession } = await import('../api');
    const ok = await tryRefreshSession();
    if (ok) {
      accessToken = localStorage.getItem('token');
    }
  }
  if (!accessToken) {
    localStorage.removeItem('userInfo');
    clearTokenCache();
    return null;
  }
  try {
    const response = await api.post('/api/v1/auth/verify-token', { token: accessToken });
    if (response?.data?.valid && response?.data?.user) {
      const user = response.data.user;
      persistUserInfoFromVerifyTokenUser(user);
      tokenCache = user;
      return user;
    }
    localStorage.removeItem('userInfo');
    clearTokenCache();
    return null;
  } catch {
    localStorage.removeItem('userInfo');
    clearTokenCache();
    return null;
  }
}

export async function verifyToken(token) {
  let accessToken = token ?? localStorage.getItem('token');
  if (!accessToken) {
    const { tryRefreshSession } = await import('../api');
    const ok = await tryRefreshSession();
    if (ok) {
      accessToken = localStorage.getItem('token');
    }
  }
  if (!accessToken) {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userInfo');
    throw new Error('Invalid token');
  }
  try {
    const response = await api.post('/api/v1/auth/verify-token', { token: accessToken });
    if (response.data.valid) {
      const user = response.data.user;
      persistUserInfoFromVerifyTokenUser(user);
      tokenCache = user;
      return user;
    }
    throw new Error('Invalid token');
  } catch (error) {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userInfo');
    throw error;
  }
}

export function getDecodedToken() {
  return tokenCache;
}

// Use this to clear cache on logout
export function clearTokenCache() {
  tokenCache = null;
}
